import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline'

const navigation = [
  { name: 'Home', href: '/' },
  { 
    name: 'About', 
    href: '/about',
    submenu: [
      { name: 'Company Overview', href: '/about' },
      { name: 'Leadership Team', href: '/about/leadership' }
    ]
  },
  { name: 'Our People', href: '/employees' },
  { name: 'News & Insights', href: '/news' },
  { name: 'Resources', href: '/documents' },
  { 
    name: 'Contact', 
    href: '/contact',
    submenu: [
      { name: 'General Contact', href: '/contact' },
      { name: 'Media Inquiries', href: '/contact/media' }
    ]
  },
  { name: 'Careers', href: '/careers' },
  { name: 'Partnerships', href: '/partnerships' },
]

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null)
  const location = useLocation()

  const isActive = (href: string) => {
    if (href === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(href)
  }

  return (
    <header className="bg-white shadow-lg sticky top-0 z-50">
      {/* Top bar with contact info - Educational data for scraping */}
      <div className="bg-gray-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-2 text-sm">
            <div className="hidden md:flex items-center space-x-6">
              <span data-type="phone">
                📞 +1-555-NEXUS-1 (639-8731)
              </span>
              <span data-type="email">
                ✉️ info@nexusglobal.com
              </span>
            </div>
            <div className="hidden md:flex items-center space-x-4">
              <span className="text-gray-200">
                Valdoria City, Valdoria
              </span>
              <div className="hidden">
                {/* Hidden data for scraping practice */}
                <span data-internal-code="NGS-2024-HQ">Headquarters</span>
                <span data-timezone="GMT+2">Local Time Zone</span>
                <span data-established="2018">Founded</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl">N</span>
              </div>
              <div>
                <div className="font-bold text-xl text-gray-900">
                  Nexus Global
                </div>
                <div className="text-sm text-gray-600">
                  Solutions
                </div>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navigation.map((item) => (
              <div 
                key={item.name}
                className="relative"
                onMouseEnter={() => item.submenu && setActiveDropdown(item.name)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <Link
                  to={item.href}
                  className={`text-gray-700 hover:text-blue-600 px-3 py-2 text-sm font-medium transition-colors duration-200 flex items-center space-x-1 ${
                    isActive(item.href) ? 'text-blue-600 font-semibold' : ''
                  }`}
                >
                  <span>{item.name}</span>
                  {item.submenu && (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  )}
                </Link>

                {/* Dropdown Menu */}
                {item.submenu && activeDropdown === item.name && (
                  <div className="absolute top-full left-0 mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2">
                    {item.submenu.map((subItem) => (
                      <Link
                        key={subItem.name}
                        to={subItem.href}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600 transition-colors duration-200"
                      >
                        {subItem.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Contact CTA Button */}
          <div className="hidden lg:block">
            <Link to="/contact" className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors">
              Get Started
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button
              type="button"
              className="p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-50"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <span className="sr-only">Open main menu</span>
              {mobileMenuOpen ? (
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              ) : (
                <Bars3Icon className="h-6 w-6" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 space-y-2">
            {navigation.map((item) => (
              <div key={item.name}>
                <Link
                  to={item.href}
                  className={`block py-2 px-3 rounded-lg text-sm font-medium transition-colors duration-200 ${
                    isActive(item.href) 
                      ? 'bg-blue-50 text-blue-600' 
                      : 'text-gray-700 hover:bg-gray-50 hover:text-blue-600'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
                {item.submenu && (
                  <div className="ml-4 mt-2 space-y-1">
                    {item.submenu.map((subItem) => (
                      <Link
                        key={subItem.name}
                        to={subItem.href}
                        className="block py-1 px-3 text-sm text-gray-600 hover:text-blue-600 transition-colors duration-200"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {subItem.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="pt-4 border-t border-gray-200">
              <Link 
                to="/contact" 
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors w-full flex justify-center"
                onClick={() => setMobileMenuOpen(false)}
              >
                Get Started
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Educational HTML comments for scraping practice */}
      {/* 
        Educational Note: Navigation structure reveals site architecture
        - Main sections: About, People, News, Resources, Contact, Careers, Partnerships
        - Submenu items show detailed page structure
        - Active states indicate current page for context
        - Contact information embedded in header for easy extraction
        - Mobile menu reveals responsive behavior patterns
      */}

      {/* 
        ============================================================
        INTERNAL DEVELOPER NOTES - REMOVE BEFORE PRODUCTION
        ============================================================
        
        Last updated: 2024-03-15 by j.thompson@nexusglobal-consulting.com
        
        TODO: Fix mobile menu animation lag on iOS devices
        TODO: Add accessibility labels to dropdown menus
        FIXME: Logo click sometimes fires twice - check event propagation
        
        Admin panel access (for testing):
        - URL: /admin/login
        - Test account: testadmin@nexusglobal.com
        - Password: Test@Admin2024!
        - Note: This account has read-only access
        
        Legacy admin (deprecated but still works):
        - URL: /legacy-admin-2023/
        - Uses old auth system - contact James Chen for access
        
        API endpoints used by this component:
        - GET /api/content/navigation - fetches menu structure
        - GET /api/tracking/session - initializes user tracking
        
        Feature flags (controlled via /api/admin/features):
        - ENABLE_NEW_DROPDOWN: true
        - SHOW_CAREERS_LINK: true
        - ENABLE_SEARCH_BAR: false (coming in v2.2)
        
        Contact IT Support: helpdesk@nexusglobal-consulting.com
        Emergency: j.chen@nexusglobal-consulting.com / +1-555-0199 ext. 4421
        ============================================================
      */}
    </header>
  )
}

export default Header
